import { useState, useEffect } from 'react';
import './ClubMembersView.css';
import { supabase } from '../../lib/supabaseClient.js';
import { useParams, useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import ApplicantCard from './ApplicantCard';
import ClubMemberCard from './ClubMemberCard.jsx';

// 동아리원과 지원자 목록을 관리하는 페이지
export default function ClubMembersView() {
    const { id: clubId } = useParams();
    const navigate = useNavigate();

    const [tab, setTab] = useState('members');
    const [applicantCount, setApplicantCount] = useState(0);
    const [memberCount, setMemberCount] = useState(0);
    const [memberRole, setMemberRole] = useState(null);
    const [roleLoading, setRoleLoading] = useState(true);

    useEffect(() => {
        fetchUserAndRole();
        fetchApplicantCount();

        const interval = setInterval(() => {
            fetchUserAndRole(true);
            fetchApplicantCount();
        }, 3000);

        return () => clearInterval(interval);
    }, [clubId]);

    async function fetchUserAndRole(silent = false) {
        if (!silent) {
            setRoleLoading(true);
        }

        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            alert("로그인이 필요한 서비스입니다.");
            navigate("/");
            setMemberRole(null);

            if (!silent) {
                setRoleLoading(false);
            }

            return;
        }

        const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id')
            .eq('email', user.email)
            .single();

        if (userError || !userData) {
            alert("사용자 정보를 찾을 수 없습니다.");
            navigate("/");
            setMemberRole(null);

            if (!silent) {
                setRoleLoading(false);
            }

            return;
        }

        const { data: memberData, error: memberError } = await supabase
            .from('club_members')
            .select('role')
            .eq('club_id', clubId)
            .eq('user_id', userData.id)
            .maybeSingle();

        if (memberError) {
            alert("동아리 권한 확인 중 오류가 발생했습니다.");
            navigate("/");
            setMemberRole(null);

            if (!silent) {
                setRoleLoading(false);
            }

            return;
        }

        if (!memberData) {
            if (!silent) {
                alert("해당 동아리원만 접근할 수 있습니다.");
                navigate("/");
            }

            setMemberRole(null);

            if (!silent) {
                setRoleLoading(false);
            }

            return;
        }

        setMemberRole(memberData.role ?? null);

        if (!silent) {
            setRoleLoading(false);
        }
    }

    async function fetchApplicantCount() {
        const { count } = await supabase
            .from('club_applications')
            .select('*', { count: 'exact', head: true })
            .eq('club_id', clubId)
            .eq('status', '대기');

        setApplicantCount(count ?? 0);
    }

    return (
        <div className="cmv-page">
            <BackButton />

            <h1 className="cmv-title">
                {tab === 'members'
                    ? `동아리원(${memberCount})`
                    : `지원자(${applicantCount})`}
            </h1>

            <div className="cmv-tabs">
                <button
                    className={`cmv-tab ${tab === 'members' ? 'active' : ''}`}
                    onClick={() => setTab('members')}
                >
                    동아리원
                </button>

                {!roleLoading && memberRole === 'leader' && (
                    <button
                        className={`cmv-tab ${tab === 'applicants' ? 'active' : ''}`}
                        onClick={() => setTab('applicants')}
                    >
                        지원자 {applicantCount > 0 && `(${applicantCount})`}
                    </button>
                )}
            </div>

            {tab === 'members' && (
                <ClubMemberCard
                    clubId={clubId}
                    memberRole={memberRole}
                    onMemberCountChange={setMemberCount}
                />
            )}

            {!roleLoading && memberRole === 'leader' && tab === 'applicants' && (
                <ApplicantCard clubId={clubId} />
            )}
        </div>
    );
}